import { useMemo } from 'react';
import type {
  DateFilter,
  MatchListValue,
  NumberPredicate,
} from '@/components/tree-dnd/tree-dnd-types';
import type { MatchListOption } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import {
  formatDateFilterSummary,
  formatMatchListSummary,
  formatNaturalMatchList,
  formatNumberPredicateSummary,
} from '@/components/tree-dnd/tree-dnd-conditions/condition-summary-format';
import { formatCurrency } from '@/components/metrics/formatters';
import { useI18nContext } from '@/i18n/i18n-react';

export function useMatchListSummary(
  value: MatchListValue,
  options: MatchListOption[],
  empty: string,
  hideMatchMode = false
) {
  const { LL } = useI18nContext();

  return useMemo(
    () =>
      formatMatchListSummary(value, options, {
        empty,
        selectedCount: ({ count }) =>
          LL.audiences.filters.selectedCount({ count }),
        matchAnyOf: ({ list }) =>
          LL.audiences.filters.conditionSummary.matchAnyOf({ list }),
        matchAllOf: ({ list }) =>
          LL.audiences.filters.conditionSummary.matchAllOf({ list }),
        hideMatchMode,
      }),
    [LL, empty, hideMatchMode, options, value]
  );
}

type NaturalMatchListOverrides = {
  manyAll?: (arg: { count: number }) => string;
  manyAny?: (arg: { count: number }) => string;
};

/** Natural list for sentences: "x, y e z" (all) / "x, y ou z" (any); 3+ uses count phrasing. */
export function useNaturalMatchListSummary(
  value: MatchListValue,
  options: MatchListOption[],
  empty: string,
  overrides?: NaturalMatchListOverrides
) {
  const { LL } = useI18nContext();

  return useMemo(
    () =>
      formatNaturalMatchList(value, options, {
        empty,
        and: LL.audiences.filters.conditionSummary.listConjunctionAnd(),
        or: LL.audiences.filters.conditionSummary.listConjunctionOr(),
        manyAll:
          overrides?.manyAll ??
          (({ count }) =>
            LL.audiences.filters.conditionSummary.listManyAll({ count })),
        manyAny:
          overrides?.manyAny ??
          (({ count }) =>
            LL.audiences.filters.conditionSummary.listManyAny({ count })),
      }),
    [LL, empty, options, overrides, value]
  );
}

export function useNumberPredicateSummary(value: NumberPredicate) {
  const { LL } = useI18nContext();

  return useMemo(
    () =>
      formatNumberPredicateSummary(value, {
        exact: ({ value }) =>
          LL.audiences.filters.number.exactSummary({ value }),
        atLeast: ({ value }) =>
          LL.audiences.filters.number.atLeastSummary({ value }),
        atMost: ({ value }) =>
          LL.audiences.filters.number.atMostSummary({ value }),
      }),
    [LL, value]
  );
}

const DEFAULT_CURRENCY = 'BRL';

export function useTotalSpendSummary(value: NumberPredicate) {
  const { LL, locale } = useI18nContext();

  return useMemo(() => {
    const amount = formatCurrency(value.value, locale, DEFAULT_CURRENCY);

    switch (value.kind) {
      case 'exact':
        return LL.audiences.filters.totalSpend.exactSummary({ amount });
      case 'atLeast':
        return LL.audiences.filters.totalSpend.atLeastSummary({ amount });
      case 'atMost':
        return LL.audiences.filters.totalSpend.atMostSummary({ amount });
    }
  }, [LL, locale, value]);
}

export function useDateFilterSummary(value: DateFilter) {
  const { LL } = useI18nContext();

  return useMemo(
    () =>
      formatDateFilterSummary(value, {
        anytime: LL.audiences.filters.date.anytime(),
        onSendDay: LL.audiences.filters.date.onSendDay(),
        dayBeforeSend: LL.audiences.filters.date.dayBeforeSend(),
        last7Days: LL.audiences.filters.date.last7Days(),
        last30Days: LL.audiences.filters.date.last30Days(),
        withinLast: ({ amount, unit }) =>
          LL.audiences.filters.date.withinLastSummary({ amount, unit }),
        notLast7Days: LL.audiences.filters.date.notLast7Days(),
        notLast30Days: LL.audiences.filters.date.notLast30Days(),
        notWithinLast: ({ amount, unit }) =>
          LL.audiences.filters.date.notWithinLastSummary({ amount, unit }),
        betweenDates: ({ from, to }) =>
          LL.audiences.filters.date.betweenDatesSummary({ from, to }),
        afterDate: ({ date }) =>
          LL.audiences.filters.date.afterDateSummary({ date }),
        beforeDate: ({ date }) =>
          LL.audiences.filters.date.beforeDateSummary({ date }),
        relativeBefore: ({ bound }) =>
          LL.audiences.filters.date.relativeBeforeSummary({ bound }),
        relativeAfter: ({ bound }) =>
          LL.audiences.filters.date.relativeAfterSummary({ bound }),
        pickDate: LL.audiences.filters.date.pickDate(),
        days: LL.audiences.filters.date.days(),
        weeks: LL.audiences.filters.date.weeks(),
        months: LL.audiences.filters.date.months(),
        years: LL.audiences.filters.date.years(),
        beforeSend: LL.audiences.filters.date.beforeSend(),
        boundEpoch: LL.audiences.filters.date.boundEpoch(),
      }),
    [LL, value]
  );
}
